import { useDisableTeam } from "@/components/page/disableTeam";
import { useNavigate, useOutletContext } from "react-router-dom";
import type { MeetingRunContext } from "./MeetingRunContext";
import {
  useImmediateMutationSystem,
  type Mutator,
} from "@/lib/queuedMutations";
import { getVoters } from "./utilities";
import { Button } from "@repo/ui/components/Base/Button";
import { ChevronLeftIcon } from "lucide-react";
import { MotionListRenderer } from "../MotionRenderer";
import { AppUrlPath } from "@/lib/AppUrlPath";
import { meetingRepo, type MeetingDto } from "@/repositories/meetingRepo";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";

export const MeetingRunMotionsSection = () => {
  useDisableTeam();

  const {
    tenantId,
    meeting: _meeting,
    team,
    userMap,
    mutationSystem,
    motionTypeMap,
  } = useOutletContext() as MeetingRunContext;

  const mutationKey = "meeting:" + _meeting.id;

  const meetingMutation: Mutator<MeetingDto> = useFutureCallback(
    async (id, data) => {
      await meetingRepo.update(tenantId, id, data);
    }
  );
  const meeting = useImmediateMutationSystem(
    mutationSystem,
    mutationKey,
    _meeting
  );

  const navigate = useNavigate();

  const votingUsers = getVoters({ meeting, team, userMap });

  return (
    <div className="flex-grow w-full h-0 overflow-y-auto pr-2 pt-1">
      <div className="w-full flex flex-col gap-y-2">
        <div className="flex flex-row gap-x-4">
          <Button
            variant="ghost"
            className="w-10 h-10 p-0 -mt-0.5 bg-white absolute shadow-[0_0_6px_2px_white]"
            onClick={() => navigate(-1)}
          >
            <ChevronLeftIcon />
          </Button>
          <h1 className="text-3xl mb-2 ml-12">Meeting Motions</h1>
        </div>

        <MotionListRenderer
          motionTypes={motionTypeMap.meeting}
          context={{
            motionText: "(You should not be seeing this)",
            meeting: meeting,
            mutationSystem,
          }}
          team={team}
          tenantId={tenantId}
          motions={meeting.review?.motions ?? []}
          votingUsers={votingUsers.all}
          absentVoters={votingUsers.absent}
          onChange={() => {
            mutationSystem.debounce(
              mutationKey,
              meetingMutation,
              {
                review: {
                  ...meeting.review,
                  motions: [...(meeting.review?.motions ?? [])],
                },
              },
              meeting.id
            );
          }}
          onLeave={async () => {
            await mutationSystem.processAll();
            navigate(new AppUrlPath(tenantId).getMeetingPath(meeting.id));
          }}
        />
      </div>
    </div>
  );
};
